import type { MemoryConflict, MemoryInvalid, MemoryStatusValue } from "@lafayette/schemas";

import { describeStaffFailure, NEUTRAL_FORBIDDEN_MESSAGE, STAFF_WORDS } from "../failure-words";
import { conflictStatusPhrase } from "./labels";

// How the pages tell a staff member what went wrong. Three rules hold here:
//   1. No value, no customer text and no id goes into a message, a log or an error report. The API
//      sends none either (an error holds a code and, for a refused value, the NAME of the field).
//   2. A refusal for access (403) and a missing id (404) read the same. The page never says whether a
//      customer exists or whether the staff member is "not assigned".
//   3. A conflict says in plain words that the memory changed and that the page was reloaded.

export type MemoryFailureKind =
  | "conflict"
  | "invalid"
  | "bad_request"
  | "forbidden"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type MemoryFailure = Readonly<{
  kind: MemoryFailureKind;
  message: string;
  /** The same command may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The page must read the memory again: what the user saw is out of date. */
  reload: boolean;
  /** For a conflict: the code of the refusal. */
  conflictCode: MemoryConflict["code"] | null;
  /** For a 422: the name of the refused field. */
  invalidField: MemoryInvalid["field"] | null;
  requestId: string | null;
}>;

const FIELD_WORDS: Readonly<Record<MemoryInvalid["field"], string>> = {
  kind: "kind",
  value: "value",
  expectedVersion: "version",
  reasonCode: "reason",
  sourceType: "source",
  sourceReference: "trip or request reference",
  requestedStatus: "starting status",
  disabledKinds: "disabled kinds",
  paused: "pause setting",
  other: "request",
};

const invalidFieldWord = (field: MemoryInvalid["field"]): string => FIELD_WORDS[field];

function conflictMessage(code: MemoryConflict["code"], status: MemoryStatusValue | null): string {
  switch (code) {
    case "memory.version_conflict":
      return "This memory changed on another device or by someone else after you opened it. The page now shows the latest version. Check it, then do the action again if it is still needed.";
    case "memory.transition_invalid":
      return `This action is not possible for the memory as it is now. The page now shows the latest state.${conflictStatusPhrase(status)}`;
    case "memory.duplicate_open_fact":
      return "This customer already has an open memory with this value. The page now shows the latest state.";
    case "memory.tombstoned":
      return "This memory was already deleted. The page now shows the latest state.";
  }
}

export function describeMemoryError(error: unknown): MemoryFailure {
  const { api, ...shared } = describeStaffFailure(error, {
    forbidden: NEUTRAL_FORBIDDEN_MESSAGE,
    conflict: ({ detail }) =>
      detail.memoryConflict === null
        ? "This memory changed. The page now shows the latest state."
        : conflictMessage(detail.memoryConflict.code, detail.memoryConflict.currentStatus),
    invalid: ({ detail }) =>
      detail.invalidField === null
        ? STAFF_WORDS.invalid
        : `The server did not accept the ${invalidFieldWord(detail.invalidField)}. Check it and try again.`,
  });
  return {
    ...shared,
    conflictCode: shared.kind === "conflict" ? (api.detail.memoryConflict?.code ?? null) : null,
    invalidField: shared.kind === "invalid" ? api.detail.invalidField : null,
  };
}
